/**
 * useGenerationRun — drives the generation screen over the REAL OpenAI
 * pipeline (offline mock by default). It calls the active engine (V1, or V2
 * when REACT_APP_AI_ENGINE=v2) with an AbortController, reveals the pipeline
 * stages, and delivers real editable templates. On failure it surfaces an
 * error (with retry); on cancel it aborts the request.
 */
import { useEffect, useRef, useState } from 'react';
import { generateWithActiveEngine } from '../aiEngine';

export const OPENAI_STAGES = [
  { id: 'brief', label: 'Preparing brief' },
  { id: 'openai', label: 'OpenAI designing concepts' },
  { id: 'compile', label: 'Compiling to editable templates' },
  { id: 'validate', label: 'Quality validation' },
  { id: 'completed', label: 'Completed' },
];

const STAGE_REVEAL_MS = 500;

export const useGenerationRun = ({ selections, brand, settings, count = 1, provider, onComplete, onError, runToken = 0 }) => {
  const lastIndex = OPENAI_STAGES.length - 1;
  const [stageIndex, setStageIndex] = useState(0);
  const [error, setError] = useState(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;
  const controllerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setStageIndex(0);
    const controller = new AbortController();
    controllerRef.current = controller;

    // Reveal stages up to the step before "Completed" while the request runs.
    const timer = setInterval(() => {
      setStageIndex((i) => Math.min(i + 1, lastIndex - 1));
    }, STAGE_REVEAL_MS);

    generateWithActiveEngine(selections, count, { signal: controller.signal, provider, brand, settings })
      .then(({ cards }) => {
        if (cancelled) return;
        clearInterval(timer);
        setStageIndex(lastIndex);
        onCompleteRef.current?.(cards);
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        clearInterval(timer);
        setError(err);
        onErrorRef.current?.(err);
      });

    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runToken]);

  const cancel = () => controllerRef.current?.abort();

  return {
    stages: OPENAI_STAGES,
    stageIndex,
    stage: OPENAI_STAGES[stageIndex],
    percent: Math.round((stageIndex / lastIndex) * 100),
    error,
    cancel,
  };
};

export { STAGE_REVEAL_MS };
